import type { ReactNode } from "react";

import { LogoFull } from "@/shared/ui/brand/LogoFull";

import { EcosystemIllustration } from "./EcosystemIllustration";

/**
 * Shared branded chrome for every auth screen (login, OTP, login-with-token)
 * — the codonix "PLX new-version" design in PayGate tokens: brand background
 * flourish + money trend line, a typographic hero, and a glowing form card.
 * Screens render their form inside <AuthCard> within <AuthShell>.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <BackgroundFlourish />

      <header className="relative z-10 flex items-start justify-between px-8 pt-8 sm:px-12">
        {/* Full brand lockup (name + tagline) in the top-left corner — the
            only logo on the auth screens (login / OTP / token). */}
        <LogoFull height={56} align="start" />
        <span className="hidden text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground sm:inline">
          Payment workspace
        </span>
      </header>

      {/* Wording, then the ecosystem, then the form. The middle column only
          appears at xl, where there is width for it to be legible. */}
      <main className="relative z-10 grid flex-1 grid-cols-1 items-center gap-10 px-8 py-8 sm:px-12 lg:grid-cols-[1fr_minmax(320px,380px)] lg:gap-12 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)_minmax(320px,370px)]">
        <BrandHero />
        <EcosystemIllustration className="hidden w-full min-w-0 xl:block" />
        <div className="w-full lg:order-last">{children}</div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-8 pb-6 text-xs text-muted-foreground sm:px-12">
        <span>
          © {new Date().getFullYear()} PayGate LogistiX · A Health Logistics Group company
        </span>
        <span className="hidden sm:inline">HIPAA-aware · v2.0</span>
      </footer>
    </div>
  );
}

/** The glowing form card with a brand top-ribbon and title/subtitle header. */
export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/15 bg-card p-8 shadow-xl shadow-primary/20 sm:p-10">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-cyan-500 via-primary to-primary-700"
      />
      <div className="mb-8 space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

// ─── Background flourish (soft brand orbs + money trend line) ───────────────────
function BackgroundFlourish() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-background to-brand-cyan-100/40" />
      <div className="absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-brand-cyan-300/30 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-[24rem] w-[24rem] rounded-full bg-primary-200/40 blur-3xl" />
      <MoneyTrend className="absolute inset-x-0 bottom-[10%] h-40 w-full opacity-[0.45]" />
    </div>
  );
}

// Rising revenue / payments trend line with a soft area fill.
function MoneyTrend({ className }: { className?: string }) {
  const pts = [
    [0, 84],
    [150, 72],
    [300, 78],
    [450, 58],
    [600, 64],
    [750, 42],
    [900, 48],
    [1050, 24],
    [1200, 30],
  ];
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const area = `${line} L 1200 100 L 0 100 Z`;

  return (
    <svg
      className={className}
      viewBox="0 0 1200 100"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="authMoneyTrendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#authMoneyTrendFill)" stroke="none" />
      <path
        d={line}
        fill="none"
        stroke="var(--primary)"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Brand hero (typographic, left column) ──────────────────────────────────────
const HERO_FEATURES = [
  { title: "Employer invoicing", detail: "Premium invoices, statements & dunning" },
  { title: "Agent commissions", detail: "Splits, ledgers & payout approvals" },
  { title: "Real-time provider payments", detail: "Paid the moment claims settle" },
  { title: "Reconciliation", detail: "Every dollar matched, end to end" },
];

function BrandHero() {
  return (
    <div className="relative z-10 hidden max-w-xl flex-col justify-center space-y-6 lg:flex">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
        Healthcare payments platform
      </p>
      <h2 className="text-4xl font-semibold leading-tight tracking-tight text-foreground">
        All your payments,
        <br />
        one <span className="text-primary">PayGate</span> workspace.
      </h2>
      <p className="text-base leading-relaxed text-muted-foreground">
        Invoice employers, collect premiums, split agent commissions, and pay providers the moment
        claims settle — reconciled end to end for the whole healthcare industry.
      </p>

      <dl className="grid grid-cols-1 gap-x-10 gap-y-4 pt-1 sm:grid-cols-2">
        {HERO_FEATURES.map(({ title, detail }) => (
          <div key={title}>
            <dt className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              {title}
            </dt>
            <dd className="pl-3.5 text-xs leading-relaxed text-muted-foreground">{detail}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
